import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import ManagementHeader from '../management/ManagementHeader';
import { useTranslation } from '../../context/LanguageContext';
import { fetchLabelQueue, fetchLabelDetail, postLabelReview, fetchLabelPipeline } from '../../services/api';
import { LabelBadge, ReliabilityDetail, StatusTag } from '../monitoring/machine/AutoLabelNew';
import '../management/management.css';
import '../monitoring/machine/AutoLabelNew.css';
import './LabelReviewPage.css';

/* ===== AI 분석 > 라벨 검토 =====
 * Auto Labeling Pipeline 이 스스로 확정하지 못한 샷(신뢰도 < 0.85)과 감사 표본(자동 확정분 2%)을 작업자가 확정한다.
 * 감사 표본 먼저, 그다음 신뢰도 낮은 순(능동학습). 확정한 라벨은
 *   ① 자동 라벨 일치율(목표 85%) ② 융합 가중치 학습(fusion.label) ③ 재학습 학습셋 에 쓰인다.
 * 단축키: 1 양품 · 2 버 · 3 웰드라인 · 4 미성형 · 5 수축 · 6 공정 기인 · S 보류 · ←/→ 이전/다음
 * 스타일: lr- (페이지) + al- (자동라벨링 탭과 공용 조각) */

const CHOICES = [
  ['OK', '1'], ['FLASH', '2'], ['WELDLINE', '3'], ['SHORT', '4'], ['SINK', '5'], ['PROCESS', '6'], ['SKIP', 'S'],
];
const STAGES = ['collect', 'inspect', 'label', 'verify', 'drift', 'retrain'];
const VISION_IMAGE_VERSION = 2;
const imageSrc = (file) => (file ? `/vision/${file}?v=${VISION_IMAGE_VERSION}` : null);
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

/* 직전 20샷 점수 추이 — 공정·비전 두 줄 + 양품 3σ 상한 + 현재 샷 */
function TrendChart({ trend, baseline, tr }) {
  const W = 520, H = 150, P = 24;
  if (!trend || trend.length < 2) return <div className="lr-chart-empty">{tr('noTrend')}</div>;
  const x = (i) => P + (i * (W - 2 * P)) / (trend.length - 1);
  const y = (v) => H - P - Math.max(0, Math.min(1, Number(v) || 0)) * (H - 2 * P);
  const line = (key) => trend.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ');
  const upP = baseline?.meanP != null ? Number(baseline.meanP) + 3 * Number(baseline.stdP) : null;
  const upV = baseline?.meanV != null ? Number(baseline.meanV) + 3 * Number(baseline.stdV) : null;
  const cur = trend.findIndex((p) => p.current);
  return (
    <svg className="lr-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      {[0, 0.5, 1].map((g) => (
        <g key={g}>
          <line x1={P} x2={W - P} y1={y(g)} y2={y(g)} className="lr-grid" />
          <text x={4} y={y(g) + 4} className="lr-axis">{g}</text>
        </g>
      ))}
      {upP != null && <line x1={P} x2={W - P} y1={y(upP)} y2={y(upP)} className="lr-sigma lr-sigma-p" />}
      {upV != null && <line x1={P} x2={W - P} y1={y(upV)} y2={y(upV)} className="lr-sigma lr-sigma-v" />}
      {cur >= 0 && <rect x={x(cur) - 7} y={P - 6} width={14} height={H - 2 * P + 12} className="lr-cur" />}
      <path d={line('processScore')} className="lr-line lr-line-p" />
      <path d={line('visionScore')} className="lr-line lr-line-v" />
      {trend.map((p, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(p.processScore)} r={p.current ? 4 : 2.2} className="lr-pt-p" />
          <circle cx={x(i)} cy={y(p.visionScore)} r={p.current ? 4 : 2.2} className="lr-pt-v" />
        </g>
      ))}
    </svg>
  );
}

function LabelReviewPage() {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.ai.labelReview.${key}`, params), [t]);
  const ta = useCallback((key, params) => t(`pages.monitoring.machine.autoLabel.${key}`, params), [t]);
  const [searchParams] = useSearchParams();
  const [equipmentId, setEquipmentId] = useState(searchParams.get('equipmentId') || '');
  const [queue, setQueue] = useState(null);
  const [index, setIndex] = useState(0);
  const [detail, setDetail] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [doneCount, setDoneCount] = useState(0);

  const loadQueue = useCallback(async () => {
    try {
      const q = await fetchLabelQueue({ equipmentId: equipmentId || undefined, limit: 100 });
      setQueue(q || { items: [], equipments: [], total: 0, audit: 0 });
      setIndex(0);
    } catch (e) {
      console.error('Failed to load label queue:', e);
      setQueue({ items: [], equipments: [], total: 0, audit: 0 });
    }
  }, [equipmentId]);

  const loadPipeline = useCallback(async () => {
    try { setPipeline(await fetchLabelPipeline()); } catch (e) { console.error('Failed to load pipeline:', e); }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);
  useEffect(() => { loadPipeline(); const id = setInterval(loadPipeline, 30000); return () => clearInterval(id); }, [loadPipeline]);

  const items = useMemo(() => queue?.items || [], [queue]);
  const current = items[index] || null;

  useEffect(() => {
    let alive = true;
    setDetail(null);
    if (!current) return undefined;
    fetchLabelDetail(current.shotUuid).then((d) => { if (alive) setDetail(d); }).catch(() => {});
    return () => { alive = false; };
  }, [current]);

  const submit = useCallback(async (label) => {
    if (!current || saving) return;
    setSaving(true);
    try {
      await postLabelReview(current.shotUuid, label);
      setDoneCount((n) => n + 1);
      setMessage({ ok: true, text: tr('saved', { shot: current.shotCount ?? '-', label: label === 'SKIP' ? tr('choice.SKIP') : tr(`choice.${label}`) }) });
      const rest = items.filter((it) => it.shotUuid !== current.shotUuid);
      setQueue((q) => ({ ...q, items: rest, total: Math.max(0, (q?.total || 1) - 1) }));
      setIndex((i) => Math.min(i, Math.max(0, rest.length - 1)));
      if (rest.length === 0) loadQueue();
    } catch (e) {
      setMessage({ ok: false, text: tr('saveFailed') });
    } finally {
      setSaving(false);
    }
  }, [current, saving, items, tr, loadQueue]);

  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return;
      const k = e.key.toUpperCase();
      const hit = CHOICES.find(([, key]) => key === k);
      if (hit) { e.preventDefault(); submit(hit[0]); }
      else if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submit, items.length]);

  const stageMap = useMemo(() => Object.fromEntries((pipeline?.stages || []).map((s) => [s.key, s])), [pipeline]);
  const ag = pipeline?.agreement;
  const it = detail?.item || current;

  return (
    <MainLayout>
      <div className="mg-page" style={{ overflow: 'hidden' }}>
        <ManagementHeader rootLabel={t('sidebar.ai.title')} icon="text_fields_alt" crumbs={[{ label: t('sidebar.ai.labelReview') }]} />
        <div className="lr-root">
          {/* 파이프라인 6단계 */}
          <div className="lr-pipeline">
            {STAGES.map((k, i) => (
              <React.Fragment key={k}>
                {i > 0 && <span className="material-symbols-outlined lr-arrow">chevron_right</span>}
                <div className={`lr-stage lr-stage-${k}`} title={tr(`stageHint.${k}`)}>
                  <span className="lr-stage-name">{i + 1}. {tr(`stage.${k}`)}</span>
                  <span className="lr-stage-count">{(stageMap[k]?.count ?? 0).toLocaleString()}</span>
                  <span className="lr-stage-note">{tr(`stageNote.${k}`, { n: Number(stageMap[k]?.note || 0).toLocaleString() })}</span>
                </div>
              </React.Fragment>
            ))}
            <div className="lr-agree" title={tr('agreeHint')}>
              <span className="lr-stage-name">{tr('agreeTitle')}</span>
              <span className="lr-stage-count">{ag?.auditRate != null ? `${Number(ag.auditRate).toFixed(1)}%` : '—'}</span>
              <span className="lr-stage-note">{tr('agreeNote', { n: (ag?.auditN ?? 0).toLocaleString(), target: num(ag?.target, 0) })}</span>
            </div>
          </div>

          <div className="lr-main">
            {/* 대기열 */}
            <aside className="lr-queue">
              <div className="lr-queue-head">
                <span className="lr-title">{tr('queueTitle')}</span>
                <span className="lr-sub">{tr('queueSub', { n: (queue?.total ?? 0).toLocaleString(), a: (queue?.audit ?? 0).toLocaleString() })}</span>
              </div>
              <select className="lr-select" value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
                <option value="">{tr('allEquipment')}</option>
                {(queue?.equipments || []).map((e) => <option key={e.id} value={e.id}>{e.name} ({e.count})</option>)}
              </select>
              <div className="lr-queue-list">
                {items.length === 0 && <div className="lr-empty">{queue ? tr('emptyQueue') : t('pages.monitoring.machine.common.loading')}</div>}
                {items.map((q, i) => (
                  <button key={q.shotUuid} type="button" className={`lr-q ${i === index ? 'active' : ''}`} onClick={() => setIndex(i)}>
                    <span className="lr-q-top">
                      <span>{q.equipmentName || `#${q.equipmentId}`} · #{q.shotCount ?? '-'}</span>
                      <span className={`lr-q-rel ${q.reviewKind === 'AUDIT' ? 'lr-q-audit' : ''}`}>
                        {q.reviewKind === 'AUDIT' ? ta('status.AUDIT') : num(q.reliability)}
                      </span>
                    </span>
                    <span className="lr-q-reason">{(q.reasons || [])[0] || ta('status.AUDIT')}</span>
                  </button>
                ))}
              </div>
              <div className="lr-done">{tr('doneN', { n: doneCount })}</div>
            </aside>

            {/* 상세 */}
            <section className="lr-detail">
              {!it ? (
                <div className="lr-empty lr-empty-big">{tr('pickOne')}</div>
              ) : (
                <>
                  <div className="lr-detail-head">
                    <span className="lr-title">{it.equipmentName || `#${it.equipmentId}`} · {tr('shotN', { n: it.shotCount ?? '-' })}</span>
                    <span className="lr-sub">{it.shotTime ? String(it.shotTime).replace('T', ' ').slice(0, 19) : ''} · {it.craftId || '-'}</span>
                    <StatusTag status={it.status} kind={it.reviewKind} tr={ta} />
                  </div>
                  <div className="lr-detail-body">
                    <div className="lr-img">
                      {imageSrc(it.imageFile) ? <img src={imageSrc(it.imageFile)} alt="" /> : <span className="al-muted">{ta('noImage')}</span>}
                    </div>
                    <div className="lr-side">
                      <div className="lr-chart-head">
                        <span>{tr('trendTitle')}</span>
                        <span className="lr-legend"><i className="lr-lg-p" />{ta('colProcess')} <i className="lr-lg-v" />{ta('colVision')} <i className="lr-lg-s" />{tr('sigmaLine')}</span>
                      </div>
                      <TrendChart trend={detail?.trend} baseline={detail?.baseline} tr={tr} />
                      <ReliabilityDetail item={it} tr={ta} />
                    </div>
                  </div>
                </>
              )}
            </section>

            {/* 확정 */}
            <aside className="lr-actions">
              <div className="lr-title">{tr('decideTitle')}</div>
              {it && (
                <div className="lr-auto">
                  <span className="al-muted">{tr('autoLabel')}</span>
                  <LabelBadge label={it.label} type={it.defectType} tr={ta} />
                </div>
              )}
              {CHOICES.map(([c, key]) => (
                <button key={c} type="button" disabled={!current || saving} onClick={() => submit(c)}
                  className={`lr-choice lr-choice-${c.toLowerCase()} ${it && ((c === 'OK' && it.label === 'OK') || c === it.defectType) ? 'lr-choice-auto' : ''}`}>
                  <span className="lr-key">{key}</span>{tr(`choice.${c}`)}
                </button>
              ))}
              {message && <div className={`lr-msg ${message.ok ? 'lr-msg-ok' : 'lr-msg-err'}`}>{message.text}</div>}
              <div className="al-note">{tr('keysNote')}</div>

              <div className="lr-title lr-req-title">{tr('requestsTitle')}</div>
              <div className="lr-reqs">
                {(pipeline?.requests || []).length === 0 && <div className="al-muted lr-req-empty">{tr('noRequests')}</div>}
                {(pipeline?.requests || []).slice(0, 6).map((r) => (
                  <div key={r.id} className="lr-req">
                    <span className="lr-req-top"><b>{tr(`kind.${r.modelKind}`)}</b><span className={`lr-req-st lr-req-${(r.status || '').toLowerCase()}`}>{tr(`reqStatus.${r.status}`)}</span></span>
                    <span className="lr-req-sub">{tr(`reason.${r.reason}`)}{r.datasetCount != null ? ` · ${r.datasetCount.toLocaleString()}${tr('cases')}` : ''}{r.craftId ? ` · ${r.craftId}` : ''}</span>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default LabelReviewPage;
